/*
 * ===============================================================================================================
 *                                Copyright 2020, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, Tooltip, IconButton } from '@mui/material';
import { Trash } from '@jda/lui-common-icon-library-mui5';
import { MRT_Row, MRT_RowData } from 'material-react-table';
import { ROWS_PER_PAGE_LABEL, DELETE_TITLE, DIALOUGE_CONTENT, DIALOUGE_OK, DIALOUGE_CANCEL } from './constants';
import TableWithPaginator from '../../shared/TableWithPaginator';
import { useStyles } from './styles';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import { IErrorMessage } from '../../models/Machines/ClientContracts';
import { IRoleMapping } from '../../models/RoleMappings/RoleMappingContract';
import { deleteRoleMappingRequest } from '../../services/roleMapping';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../UserDetails/constant';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import SnackbarAlert from '../../shared/SnackbarAlert';
import useWindowDimensions from '../../utility/useWindowDimensions';
import { LuiActionsMenu } from '@jda/lui-common-component-library-mui5';

const columns = [
  {
    header: 'Identity Provider',
    accessorKey: 'idpName'
  },
  {
    header: 'Identity Provider Role Name',
    accessorKey: 'name'
  },
  {
    header: 'Blue Yonder Role Name',
    accessorKey: 'realmName'
  }
];

const RoleMappingList = ({
  roleMappingData,
  enableRoleMapping,
  refreshOnDelete,
  onEdit,
  readPermission,
  readWritePermission,
  loading,
  extendedToolbar,
  emptyTableFallback,
}: any) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const { height } = useWindowDimensions();

  const [isPreviewShown, setPreviewShown] = useState(false);
  const [rowData, setRowData] = useState<IRoleMapping>();
  const [showLoader, showHideLoader] = useState(loading);
  const [errors, setErrors] = useState<IErrorMessage>({ message: '' });
  const [showDialogLoader, setDialogHideLoader] = useState(false);

  useEffect(() => {
    showHideLoader(loading);
  }, [loading]);

  const deleteRoleMapping = async () => {
    showHideLoader(true);
    setDialogHideLoader(true);
    removeRoleMapping(rowData);
  };

  const removeRoleMapping = async (rowData: IRoleMapping | any) => {
    setErrors({ message: '' });
    try {
      await deleteRoleMappingRequest(rowData.id);
      showHideLoader(false);
      setDialogHideLoader(false);
      setPreviewShown(false);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: `Role mapping deleted`,
          severity: 'success'
        })
      );
      refreshOnDelete();
    } catch (error: any) {
      showHideLoader(false);
      setDialogHideLoader(false);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: 'Error in removing policy: ' + error,
          severity: 'error'
        })
      );
    }
  };
  const deleteOnClick = (rowData: IRoleMapping | any) => {
    setPreviewShown(true);
    setRowData(rowData);
  };

  const customRowActions = (row: MRT_Row<MRT_RowData>) => {
    return (
      <LuiActionsMenu
        items={[
          readWritePermission && enableRoleMapping ? {
            render: (
              <Tooltip title="delete">
                <Box data-testid="toolTip-sign">
                  <IconButton
                    aria-label="delete"
                    data-testid="deleteIcon"
                    onClick={() => deleteOnClick(row.original)}
                  >
                    <Trash className={classes.deleteIcon} />
                  </IconButton>
                </Box>
              </Tooltip>
            ),
            alwaysVisible: true
          } : []
        ].flat()}
        maxItemsToDisplay={2}
      />
    )
  };

  const getDialogTitle = (rowData: any) => {
    let title = DELETE_TITLE.replaceAll('{name}', rowData.name);
    title = title.replaceAll('{realmName}', rowData.realmName);
    return title;
  };
  return (
    <Grid className={classes.card}>
      <TableWithPaginator
        columns={columns}
        loading={showLoader}
        data={roleMappingData}
        customRowActions={customRowActions}
        labelRowsPerPage={ROWS_PER_PAGE_LABEL}
        rowsPerPageOptions={[1, 5, 10]}
        maxTableBodyHeight={height - 35 * 8}
        extendedToolbar={extendedToolbar}
        onRowClick={(rowData: any) => readPermission && enableRoleMapping && onEdit(rowData)}
        renderEmptyState={emptyTableFallback}
      />
      {isPreviewShown && (
        <AlertDialog
          dialogTitle={`${rowData ? getDialogTitle(rowData) : ''}`}
          dialogContentText={DIALOUGE_CONTENT}
          recordName={''}
          primaryButtonText={DIALOUGE_OK}
          primaryButtonColor='error'
          secondButtonText={DIALOUGE_CANCEL}
          handleOnCancel={() => setPreviewShown(false)}
          handleOnOk={deleteRoleMapping}
          showDialogLoader={showDialogLoader}
          isStatement={true}
          hideCanNotBeUndoneMessage
        />
      )}
      <SnackbarAlert />
      {errors && <ErrorMessage {...errors} />}
    </Grid>
  );
};

export default RoleMappingList;
